import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Avatar } from './ui';

const LogoutButton: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (compact) {
    return (
      <button
        onClick={handleLogout}
        aria-label="Sign out"
        title="Sign out"
        className="btn-icon"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
        </svg>
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={user?.name || 'User'} size="sm" />
        <div className="hidden md:block text-left">
          <p className="text-xs font-semibold text-ink-900 dark:text-ink-50 leading-tight max-w-32 truncate">{user?.name || 'User'}</p>
          <p className="text-2xs text-ink-500 dark:text-ink-400 capitalize leading-tight">{user?.role?.replace('_', ' ')}</p>
        </div>
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-ink-400 dark:text-ink-500 hidden md:block" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 md:hidden" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="absolute right-0 mt-2 w-56 z-50 surface-elevated rounded-xl shadow-e4 overflow-hidden animate-scale-in origin-top-right"
          >
            <div className="p-3 border-b border-ink-200 dark:border-ink-800">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{user?.email}</p>
              <span className="mt-2 inline-flex pill-neutral capitalize">{user?.role?.replace('_', ' ')}</span>
            </div>
            <button
              onClick={handleLogout}
              role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-danger-600 dark:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-500/10 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LogoutButton;
